import {$generateNodesFromDOM} from '@lexical/html';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $insertNodes,
  COMMAND_PRIORITY_HIGH,
  PASTE_COMMAND,
  PasteCommandType,
} from 'lexical';
import {useEffect} from 'react';

import {useEditorRole, useIsTemplateMode} from './RoleContext';

/**
 * Plugin that intercepts paste events and converts heading elements to bold text
 * when not in template mode. In template mode, headings are preserved as enhanced headings.
 */
export const HeadingPastePlugin = () => {
  const [editor] = useLexicalComposerContext();
  const isTemplateMode = useIsTemplateMode();
  const {isEditable} = useEditorRole();

  useEffect(() => {
    if (!isEditable) {
      return;
    }

    // Helper function to convert heading elements to bold paragraphs
    const convertHeadingsToBold = (html: string): string => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      // Find all heading elements
      const headings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6');
      headings.forEach((heading) => {
        // Clean up Lexical-specific decorator elements
        // Remove chevron decorators
        const chevrons = heading.querySelectorAll('[data-lexical-heading-chevron]');
        chevrons.forEach((chevron) => chevron.remove());

        // Remove word count decorators
        const wordCounts = heading.querySelectorAll('[data-lexical-word-count]');
        wordCounts.forEach((wordCount) => wordCount.remove());

        // Remove heading controls decorators
        const controls = heading.querySelectorAll('[data-lexical-heading-controls]');
        controls.forEach((control) => control.remove());

        // Remove guidance decorators
        const guidance = heading.querySelectorAll('[data-lexical-guidance]');
        guidance.forEach((guide) => guide.remove());

        if (isTemplateMode) {
          const copiedHeading = document.createElement(heading.tagName);
          copiedHeading.textContent = heading.textContent || '';

          heading.replaceWith(copiedHeading);
          return;
        }

        // Create a paragraph element with bold formatting
        const paragraph = document.createElement('p');

        // Create a strong tag for bold text
        const strong = document.createElement('strong');
        // Use textContent to get only the text, avoiding any remaining decorator elements
        strong.textContent = heading.textContent || '';

        paragraph.appendChild(strong);

        // Replace the heading with the paragraph
        heading.replaceWith(paragraph);
      });

      return tempDiv.innerHTML;
    };

    const removePasteListener = editor.registerCommand(
      PASTE_COMMAND,
      (event: PasteCommandType) => {
        try {
          const clipboardData = (event as any).clipboardData;
          if (!clipboardData) {
            return false;
          }

          const html = clipboardData.getData('text/html');
          if (!html) {
            return false;
          }

          // Check if the HTML contains heading elements
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;
          const headings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6');

          if (headings.length > 0) {
            // Process the HTML to convert headings to bold text
            const processedHtml = convertHeadingsToBold(html);

            // Prevent default paste behavior and manually insert the converted content
            event.preventDefault();

            // Parse the processed HTML and insert it into the editor
            const parser = new DOMParser();
            const dom = parser.parseFromString(processedHtml, 'text/html');

            editor.update(() => {
              const selection = $getSelection();
              if (selection) {
                const nodes = $generateNodesFromDOM(editor, dom);
                $insertNodes(nodes);
              }
            });

            // Return true to indicate we've handled the paste
            return true;
          }

          return false; // Let other handlers run
        } catch {
          return false;
        }
      },
      COMMAND_PRIORITY_HIGH
    );

    return () => {
      removePasteListener();
    };
  }, [editor, isEditable, isTemplateMode]);

  return null;
};
